import { useNotification } from "@expert/common-ui";
import { type JSX, useEffect, useState } from "react";

import type { CommunicationType } from "../../../../../types";

import { isMaxNotificationAttemptsError } from "../../../../../api/sendNotification.errors";
import { useSalesContext } from "../../../../../sales-context/SalesContext";
import { formatPhoneNumber, sendSalesErrorMetric } from "../../../../../shared-utils";
import { useProductOfferStore } from "../../../../../stores";
import { type OfferChecklistState, useOfferChecklistStore } from "../../../../../stores/useOfferChecklistStore";
import { useVerifiedCustomerStore } from "../../../../../stores/useVerifiedCustomerStore";
import { GuidedBrowsingModal, useGuidedBrowsingStore } from "../../../../guided-browsing";
import { useHandleSendLink } from "../../SendLink/HandleSendLink";
import { GuidedBrowsingContentView } from "./GuidedBrowsingContentView";

export function GuidedBrowsingContent({
    productSku,
    sendLinkCopy,
    sendLinkSubCopy,
    sentSuccessMessage,
    checklistPlacement = "TRANSITION",
    requiresChecklistCompletion = true,
    returnLinkToExpert = true,
    sendLinkCommType,
}: {
    productSku: string;
    sendLinkCopy?: string;
    sendLinkSubCopy?: string;
    sentSuccessMessage?: string;
    checklistPlacement?: string;
    requiresChecklistCompletion?: boolean;
    returnLinkToExpert?: boolean;
    sendLinkCommType?: string;
}): JSX.Element {
    const { sessionId, partner, logger, analytics, isSimulatedVoiceTask } = useSalesContext();
    const notification = useNotification();
    const { customer } = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId));
    const guidedBrowsingSession = useGuidedBrowsingStore((state) => state.getSession(sessionId));
    // Actions are stable refs; read once without subscribing.
    const { receiveData, openModal } = useGuidedBrowsingStore.getState();
    const data = useOfferChecklistStore((state) => state.getData(sessionId, productSku));
    const sendLink = useHandleSendLink({ isGuidedBrowsing: true });

    // Mirrors HandleSendLink.ts's own productOfferId derivation: the server's attempt counter
    // (and therefore remainingAttempts) is keyed on productOfferId + sessionId, not sessionId
    // alone — a session can have multiple eligible primary products (e.g. Cricket sessions).
    const primaryProducts = useProductOfferStore((state) => state.getPrimaryProducts(sessionId));
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));
    // Falls back to "" (rather than crashing) if this renders before the product store loads.
    const productOfferId = selectedProduct?.id ?? primaryProducts[0]?.id ?? "";
    const { remainingAttempts } = useProductOfferStore((state) =>
        state.getSmartLinkMetaBySessionId(sessionId, productOfferId),
    );

    const initialPhoneNumber = formatPhoneNumber(customer?.mdn ?? "", undefined, true).number || customer?.mdn || "";
    const [commType, setCommType] = useState<CommunicationType>((sendLinkCommType as CommunicationType) ?? "SMS");
    const [contact, setContact] = useState(commType === "EMAIL" ? (customer?.email ?? "") : initialPhoneNumber);
    const [loading, setLoading] = useState(false);

    const isEmailType = commType === "EMAIL";
    const contactForApi = isEmailType ? contact : formatInputMDN(contact);
    const deliveryMethod = isEmailType ? "email" : "sms";

    const canShowGuidedBrowsingButton =
        !requiresChecklistCompletion || areGuidedBrowsingPrerequisitesComplete(data, checklistPlacement);
    const guidedLinkSent = guidedBrowsingSession?.deliveryStatus === "sent";
    const isSendDisabled =
        (!canShowGuidedBrowsingButton && !guidedLinkSent) ||
        !contact ||
        isSimulatedVoiceTask ||
        remainingAttempts === 0;
    const showOverrideAction = !canShowGuidedBrowsingButton && !guidedLinkSent;
    const showViewSentLinkAction = returnLinkToExpert && guidedLinkSent && !guidedBrowsingSession?.isModalOpen;

    const handleContactChange = (inputValue: string) => {
        if (isEmailType) {
            setContact(inputValue);
        } else {
            setContact(formatInputMDNDisplay(inputValue));
        }
    };

    const handleCommTypeChange = (value: string) => {
        setCommType(value);
        if (value === "EMAIL") {
            setContact(customer?.email ?? "");
        } else {
            setContact(initialPhoneNumber);
        }
    };

    const handleSend = async (isOverride: boolean) => {
        setLoading(true);
        try {
            logger.info(
                { isOverride },
                isOverride
                    ? "Override: Attempting to send guided browsing link without checklist prerequisites"
                    : "Attempting to send guided browsing link",
            );
            if (isOverride) {
                void analytics.dispatcher.dispatchBusinessEvent("GuidedBrowsingOverrideUsed", {
                    sessionId,
                    partner,
                    deliveryMethod,
                });
            }
            const response = await sendLink(commType, contactForApi);

            notification.success(
                `${sentSuccessMessage || DEFAULT_SENT_SUCCESS_MESSAGE} ${getResendsAvailableCopy(response.remainingAttempts)}`,
            );
            logger.info({ url: response.url, isOverride, returnLinkToExpert }, "Guided browsing link sent");
            if (!response.url && returnLinkToExpert) {
                logger.warn({ response, isOverride }, "Guided browsing link sent but response missing url");
            }
            receiveData(sessionId, {
                url: response.url ?? "",
                sessionUrl: response.sessionUrl,
                deliveryStatus: "sent",
                deliveryMethod,
            });
            useProductOfferStore.getState().updateSmartLinkMetaBySessionId(sessionId, productOfferId, {
                maximumSmartLinksGenerated: response.maxAttemptsReached,
                remainingAttempts: response.remainingAttempts,
            });
            if (!returnLinkToExpert) {
                useGuidedBrowsingStore.getState().closeModal(sessionId);
            }
        } catch (error) {
            if (isMaxNotificationAttemptsError(error)) {
                useProductOfferStore.getState().updateSmartLinkMetaBySessionId(sessionId, productOfferId, {
                    maximumSmartLinksGenerated: true,
                    remainingAttempts: 0,
                });
                notification.error(getResendsAvailableCopy(0));
                logger.warn({ error, isOverride }, "Failed to send guided browsing link: maximum attempts reached");
                // No sendSalesErrorMetric on a cap refusal — see isMaxNotificationAttemptsError
                // in api/sendNotification.errors.ts for why, and ew#7426 for the measured volume.
            } else {
                notification.error("Failed to send guided browsing link. Please try again.");
                logger.error({ error, isOverride }, "Error sending guided browsing link");
                void sendSalesErrorMetric("GuidedBrowsingLinkError", { partner });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (commType === "EMAIL" && customer?.email) {
            setContact(customer.email);
        } else if (commType === "SMS" && customer?.mdn) {
            const formatted = formatPhoneNumber(customer.mdn, undefined, true).number || customer.mdn;
            setContact(formatted);
        }
    }, [customer?.mdn, customer?.email, commType]);

    return (
        <>
            <GuidedBrowsingContentView
                commType={commType}
                contact={contact}
                isEmailType={isEmailType}
                loading={loading}
                isSendDisabled={isSendDisabled}
                isOverrideDisabled={loading || !contact || isSimulatedVoiceTask}
                showOverrideAction={showOverrideAction}
                showViewSentLinkAction={showViewSentLinkAction}
                sendLinkCopy={sendLinkCopy}
                sendLinkSubCopy={sendLinkSubCopy}
                showCommTypeSelector={!sendLinkCommType}
                onCommTypeChange={handleCommTypeChange}
                onContactChange={handleContactChange}
                onSend={() => {
                    void handleSend(false);
                }}
                onSendOverride={() => {
                    void handleSend(true);
                }}
                onViewSentLink={() => openModal(sessionId)}
            />
            <GuidedBrowsingModal sessionId={sessionId} logger={logger} />
        </>
    );
}

const DEFAULT_SENT_SUCCESS_MESSAGE = "Guided browsing link sent successfully!";

const getResendsAvailableCopy = (remainingAttempts: number): string =>
    `${remainingAttempts} link resend${remainingAttempts === 1 ? "" : "s"} available.`;

const sanitizePhoneNumber = (phone: string | undefined): string => {
    if (!phone) return "";
    return phone.replace(/\D/g, "");
};

const formatInputMDN = (inputMdn: string | undefined): string => {
    if (!inputMdn) return "";
    return `+1${sanitizePhoneNumber(inputMdn)}`;
};

const formatInputMDNDisplay = (inputMdn: string): string => {
    const sanitized = sanitizePhoneNumber(inputMdn);
    if (sanitized.length <= 3) return `(${sanitized}`;
    if (sanitized.length <= 6) return `(${sanitized.slice(0, 3)}) ${sanitized.slice(3)}`;
    return `(${sanitized.slice(0, 3)}) ${sanitized.slice(3, 6)}-${sanitized.slice(6, 10)}`;
};

/**
 * Check if the required prerequisite checklist items are completed
 * before allowing the guided browsing button to be shown.
 */
const areGuidedBrowsingPrerequisitesComplete = (
    data: OfferChecklistState["data"],
    checklistPlacement = "TRANSITION",
): boolean => {
    return (data as Record<string, boolean>)[checklistPlacement] ?? false;
};
